import { Stack, Typography, useTheme } from '@mui/material';
import type { AgRadialBarSeriesOptions } from 'ag-charts-enterprise';
import dayjs from 'dayjs';
import { useAtom, useAtomValue } from 'jotai';
import { sumBy } from 'lodash';
import { useMemo } from 'react';

import RadialBarChart from '@/components/charts/RadialBarChart';
import VerticalBarChart from '@/components/charts/VerticalBarChart';
import REPORT_PARAGRAPHS from '@/constants/reportparagraph.constants';
import ReportFullWidthContainer from '@/features/ReportsTemplateFullWidthContainer/ReportsFullWidthContainer';
import ReportParagraph from '@/features/ReportsTemplateParagraph/ReportsParagraph';
import {
  CASES_RADIAL_BAR_SERIES,
  CASES_REPORTS_VERTICAL_BAR_SERIES,
  EDR_CASES_RADIAL_BAR_SERIES,
  LOG_CASES_RADIAL_BAR_SERIES,
  NETWORK_CASES_RADIAL_BAR_SERIES
} from '@/pages/ReportsTemplates/ReportsTemplates.constants';
import { formatLargeNumber } from '@/utils/Common.utils';

import type { CasesGroupByDTO } from '../../ReportsPages/ReportsPage.types';
import {
  reportsEndDateAtom,
  reportsTimeFrameAtom
} from '../../ReportsTemplate.hooks';
import {
  getGraphCounts,
  getURLTimeframeData
} from '../../ReportsTemplate.utils';
import useStyles from '../ReportsPages.styles';

const CasesPage1 = ({
  ticketedCasesPerDay,
  hasLog,
  hasNetwork,
  hasEDR,
  casesPerDay
}: {
  ticketedCasesPerDay: CasesGroupByDTO[];
  hasLog: boolean;
  hasNetwork: boolean;
  hasEDR: boolean;
  casesPerDay: CasesGroupByDTO[];
}) => {
  const theme = useTheme();
  const { classes } = useStyles();
  const timeframe = useAtomValue(reportsTimeFrameAtom);
  const [endDate] = useAtom(reportsEndDateAtom);
  const minTimeFrame = timeframe < 7 ? 7 : timeframe;

  const singleDayCases = getURLTimeframeData(casesPerDay, 'value');
  const singleDayTicketedCases = getURLTimeframeData(
    ticketedCasesPerDay,
    'value'
  );

  const singleDayNetworkCases = singleDayCases;
  const singleDayLogCases = singleDayCases;

  const timeFrameCorrectedCases = timeframe < 7 ? singleDayCases : casesPerDay;
  const timeFrameCorrectedTicketedCases =
    timeframe < 7 ? singleDayTicketedCases : ticketedCasesPerDay;

  const timeFrameCorrectedNetworkCases =
    timeframe < 7 ? singleDayNetworkCases : casesPerDay;
  const timeFrameCorrectedLogCases =
    timeframe < 7 ? singleDayLogCases : casesPerDay;

  // Only want log cases for the one day (startDate)
  const totalLogCases = useMemo(() => {
    return timeFrameCorrectedLogCases.reduce(
      (acc: number, caseItem) => acc + (caseItem.log_count || 0),
      0
    );
  }, [timeFrameCorrectedLogCases]);

  const totalNetworkCases = useMemo(() => {
    return timeFrameCorrectedNetworkCases.reduce(
      (acc: number, caseItem) => acc + (caseItem.network_count || 0),
      0
    );
  }, [timeFrameCorrectedNetworkCases]);

  const barGraphData = useMemo(() => {
    const finalDataSet: Record<
      string,
      { date: Date; ticketedCases?: number; cases?: number }
    > = {};

    const ticketedCases = getGraphCounts(
      minTimeFrame,
      ticketedCasesPerDay || [],
      endDate
    );

    const cases = getGraphCounts(minTimeFrame, casesPerDay || [], endDate);

    ticketedCases?.forEach((item) => {
      const dateKey = dayjs(item.date).format();

      // Add every item to finalDataSet with the specified structure
      finalDataSet[dateKey] = {
        date: dayjs(item.date).toDate(),
        ticketedCases: item.count
      };
    });

    cases?.forEach((item) => {
      const dateKey = dayjs(item.date).format();

      // If the date already exists in finalDataSet, add cases count (sum if multiple types on same day)
      if (finalDataSet[dateKey]) {
        const prevValue = finalDataSet[dateKey].cases || 0;
        finalDataSet[dateKey].cases = prevValue + item.count;
      } else {
        finalDataSet[dateKey] = {
          date: dayjs(item.date).toDate(),
          cases: item.count
        };
      }
    });

    const result = Object.values(finalDataSet);

    return result;
  }, [ticketedCasesPerDay, casesPerDay, endDate, minTimeFrame]);

  const casesGraph = useMemo(() => {
    return (
      <>
        <Typography className={classes.reportsPreviewSubTitle}>
          {`${minTimeFrame} Day Totals`}
        </Typography>
        <VerticalBarChart
          data={barGraphData}
          series={CASES_REPORTS_VERTICAL_BAR_SERIES}
          outlined
          doubleAxis
        />
      </>
    );
  }, [barGraphData, timeframe]);

  const totalTicketedCases = sumBy(timeFrameCorrectedTicketedCases, 'count');

  const totalCases = sumBy(timeFrameCorrectedCases, 'count');

  const productCheckedRadialBarData = useMemo(() => {
    const radialBarData: { [key: string]: number | string }[] = [
      {
        name: `Ticketed Cases - ${totalTicketedCases}`,
        ticketedCases: totalTicketedCases,
        remainder: totalCases > 0 ? totalCases - totalTicketedCases : 100
      }
    ];

    if (hasLog) {
      radialBarData.push({
        name: `Log Cases - ${formatLargeNumber(totalLogCases)}`,
        logCases: totalLogCases,
        remainder: totalCases > 0 ? totalCases - totalLogCases : 100
      });
    }

    if (hasNetwork) {
      radialBarData.push({
        name: `Network Cases - ${formatLargeNumber(totalNetworkCases)}`,
        networkCases: totalNetworkCases,
        remainder: totalCases > 0 ? totalCases - totalNetworkCases : 100
      });
    }

    // For EDR-only customers, show log cases as EDR cases
    if (hasEDR && !hasLog) {
      radialBarData.push({
        name: `EDR Cases - ${formatLargeNumber(totalLogCases)}`,
        edrCases: totalLogCases,
        remainder: totalCases > 0 ? totalCases - totalLogCases : 100
      });
    }

    return radialBarData;
  }, [
    totalLogCases,
    totalNetworkCases,
    totalTicketedCases,
    timeFrameCorrectedTicketedCases,
    hasLog,
    hasNetwork,
    hasEDR
  ]);

  const radialBarSeries: AgRadialBarSeriesOptions[] = useMemo(() => {
    const series = [...CASES_RADIAL_BAR_SERIES];

    // 1. Log Cases (first)
    if (hasLog && !series.find((s) => s.angleKey === 'logCases')) {
      series.unshift(LOG_CASES_RADIAL_BAR_SERIES);
    }

    // 2. Network Cases (second)
    if (hasNetwork && !series.find((s) => s.angleKey === 'networkCases')) {
      series.unshift(NETWORK_CASES_RADIAL_BAR_SERIES);
    }

    // 3. EDR Cases (third) - only when EDR is enabled and Log is not
    if (hasEDR && !hasLog && !series.find((s) => s.angleKey === 'edrCases')) {
      series.unshift(EDR_CASES_RADIAL_BAR_SERIES);
    }

    return series;
  }, [
    hasLog,
    hasNetwork,
    hasEDR,
    theme,
    CASES_RADIAL_BAR_SERIES,
    productCheckedRadialBarData
  ]);

  const radialBar = useMemo(() => {
    return (
      <RadialBarChart
        data={productCheckedRadialBarData}
        series={radialBarSeries}
        total={totalCases}
      />
    );
  }, [productCheckedRadialBarData, timeframe]);

  return (
    <div className={classes.reportsPageContainer}>
      <ReportParagraph>{REPORT_PARAGRAPHS.casesDescription}</ReportParagraph>
      <ReportFullWidthContainer title="Total Cases">
        <div className={classes.totalCasesText}>
          {formatLargeNumber(sumBy(timeFrameCorrectedCases, 'count'), true)}
        </div>
      </ReportFullWidthContainer>
      <Stack>
        <div
          data-testid="cases-radial-bar-graph"
          style={{ width: '100%', height: '225px' }}
        >
          {radialBar}
        </div>
        <div
          style={{ width: '100%', height: '300px' }}
          className={classes.reportsChartContainer}
          data-testid="cases-bar-graph"
        >
          {casesGraph}
        </div>
        <ReportParagraph fontSize="0.8rem">
          {REPORT_PARAGRAPHS.casesBarGraphDescription}
        </ReportParagraph>
      </Stack>
    </div>
  );
};
export default CasesPage1;
